<template>
  <nav class="navbar navbar-expand-lg navbar-light bg-light custom-nav" 
       :class="sidebarExpanded ? 'nav-sidebar-expanded' : 'nav-sidebar-collapsed'"
       :style="`padding: 5px; background: transparent !important;`">
    <div class="container-fluid">
      <!-- Universal Search - Positioned next to sidebar -->
      <div v-if="shouldShowUniversalSearch" class="universal-search-container">
        <UniversalSearch />
      </div>
      
      <div class="collapse navbar-collapse" id="navbarSupportedContent">
        <ul class="navbar-nav me-auto mb-2 mb-lg-0"></ul>
        <div class="d-flex align-items-center">
          <div v-if="!business.affiliate_id && businessStatus !== 'active'" class="d-flex align-items-center me-3">
            <span v-if="freeTrials > 0" class="me-3 d-none d-md-inline text-muted small fw-bold">
              {{freeTrials}} {{ $t('days_remaining') || 'days' }}
            </span>
            <router-link :to="subscribeRoute" class="btn btn-subscribe-nav btn-sm py-1 px-3 fw-bold shadow-sm">
              <AppIcon name="bi-rocket-takeoff-fill" class="bi bi-rocket-takeoff-fill me-1" />
              {{ $t('subscribe') }}
            </router-link>
          </div>
          <!-- <span class="d-inline-block text-secondary me-3" style="padding:5px;cursor:pointer" @click="showInventoriesModal">
            <AppIcon name="bi-box-seam-fill" class="bi bi-box-seam-fill" style="font-size:20px" />
          </span> -->
          <!-- <VideoCard /> -->
            <!-- AI Chat Button -->
          <!-- <button class="btn" style="padding:8px" @click="toggleAIChat">
            <span class="d-inline-block text-secondary">
              <AppIcon name="bi-robot" class="bi bi-robot" style="font-size:20px" />
            </span>
          </button> -->

          <router-link
            v-if="!business.affiliate_id"
            to="/tutorials"
            class="tutorials-nav-btn btn"
            :title="$t('tutorials')"
            :aria-label="$t('tutorials')"
          >
            <AppIcon name="bi-play-btn" class="bi bi-play-btn" style="font-size:20px" />
          </router-link>

          <div v-if="totalNotifications && (reminderCount || helper.checkAccess('view_inventories') != -1 || helper.checkAccess('view_invoices') != -1)" class="notification-dropdown dropdown dropleft">
            <button class="notification-btn btn dropdown-toggle" type="button" id="dropdownMenuButton1" data-bs-toggle="dropdown" aria-expanded="false">
              <AppIcon name="bi-bell" class="bi bi-bell" style="font-size:20px" />
              <span v-if="totalNotifications" class="notification-badge badge bg-danger">{{totalNotifications}}</span>
            </button>
            <div v-if="totalNotifications" class="dropdown-menu notification-menu" aria-labelledby="dropdownMenuButton1">
              <div class="notification-header">
                <h6 class="mb-0">{{ $t('notifications') }}</h6>
                <span class="notification-count">{{ totalNotifications }} {{ $t('alerts') }}</span>
              </div>
              <div class="notification-body">
                <!-- Personal reminders from every supported module -->
                <div v-if="reminders.length">
                  <div v-for="reminder in reminders" :key="'reminder-'+reminder.id" class="notification-item unread">
                    <div class="notification-icon">
                      <AppIcon name="bi-bell-fill" class="bi bi-bell-fill text-primary" />
                    </div>
                    <div class="notification-content">
                      <router-link :to="reminderPath(reminder)" style="text-decoration: none;">
                        <div class="notification-title">
                          <span class="product-name">{{ reminder.entity_label }}</span>
                          <span class="notification-time">{{ formatTimeAgo(reminder.remind_at) }}</span>
                        </div>
                        <div class="notification-text reminder-note">{{ reminder.note }}</div>
                      </router-link>
                      <div class="notification-status">
                        <button class="status-badge reminder-complete" type="button" @click.stop="completeReminder(reminder.id)">
                          <AppIcon name="bi-check2" class="bi bi-check2 me-1" />{{ $t('mark_as_done') }}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Due Invoices -->
                <div v-if="helper.checkAccess('view_invoices') != -1">
                  <div v-for="(invoice, index) in dueInvoices" :key="'inv-'+index" class="notification-item" :class="{'unread': true}">
                    <div class="notification-icon">
                      <AppIcon name="bi-receipt" class="bi bi-receipt text-danger" />
                    </div>
                    <div class="notification-content">
                      <router-link :to="`/invoices/${invoice.id}`" style="text-decoration: none;">
                      <div class="notification-title">
                        <span class="product-name">{{invoice.reference}}</span>
                        <span class="notification-time">{{ formatTimeAgo(invoice.due_date) }}</span>
                      </div>
                      <div class="notification-text">
                        {{ $t('client') }}: <span class="inventory-ref">{{invoice.client ? invoice.client.name : '-'}}</span>
                      </div>
                      <div class="notification-status">
                        <span class="status-badge out-of-stock">
                          <AppIcon name="bi-clock-history" class="bi bi-clock-history me-1" />{{ $t('overdue') }}
                        </span>
                      </div>
                      </router-link>
                    </div>
                  </div>
                </div>

                <!-- Expired Products -->
                <div v-if="helper.checkAccess('view_inventories') != -1">
                  <div v-for="(item, index) in expiredInventories" :key="'exp-'+index" class="notification-item" :class="{'unread': true}">
                    <div class="notification-icon">
                      <AppIcon name="bi-x-circle-fill" class="bi bi-x-circle-fill text-danger" />
                    </div>
                    <div class="notification-content">
                      <router-link :to="`/products/${item.product.id}`" style="text-decoration: none;">
                      <div class="notification-title">
                        <span class="product-name">{{item.product.label}}</span>
                        <span class="notification-time">{{ formatTimeAgo(item.expiration_date) }}</span>
                      </div>
                      <div class="notification-text">
                        {{ $t('inventory') }}: <span class="inventory-ref">{{item.reference}}</span>
                      </div>
                      <div class="notification-status">
                        <span class="status-badge out-of-stock">
                          <AppIcon name="bi-calendar-x" class="bi bi-calendar-x me-1" />{{ $t('expired_products') }}
                        </span>
                      </div>
                      </router-link>
                    </div>
                  </div>
                </div>

                <!-- Stock Alerts -->
                <div v-if="helper.checkAccess('view_inventories') != -1">
                  <div v-for="(item, index) in warning" :key="'warn-'+index" class="notification-item" :class="{'unread': !item.read}">
                    <div class="notification-icon">
                      <AppIcon name="bi-exclamation-circle-fill" v-if="item.quantity <= 0" class="bi bi-exclamation-circle-fill text-danger" />
                      <AppIcon name="bi-exclamation-triangle-fill" v-else class="bi bi-exclamation-triangle-fill text-warning" />
                    </div>
                    <div class="notification-content">
                      <router-link :to="`/products/${item.product.id}`" style="text-decoration: none;">
                      <div class="notification-title">
                        <span class="product-name">{{item.product.label}}</span>
                        <span class="notification-time">{{ formatTimeAgo(item.updated_at) }}</span>
                      </div>
                      <div class="notification-text">
                        {{ $t('inventory') }}: <span class="inventory-ref">{{item.reference}}</span>
                      </div>
                      <div class="notification-status">
                        <span v-if="item.quantity <= 0" class="status-badge out-of-stock">
                          <AppIcon name="bi-x-circle" class="bi bi-x-circle me-1" />{{ $t('out_of_stock') }}
                        </span>
                        <span v-else class="status-badge low-stock">
                          <AppIcon name="bi-arrow-down-circle" class="bi bi-arrow-down-circle me-1" />{{ $t('will_be_out_of_stock_soon') }}
                        </span>
                      </div>
                      </router-link>
                      
                    </div>
                  </div>
                </div>
                
                <!-- Loading state -->
                <div v-if="isLoadingMoreNotifications" class="text-center py-3">
                  <div class="spinner-border spinner-border-sm text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                </div>
                
                <!-- No results message when applicable -->
                <div v-if="totalNotifications === 0 && !isLoadingMoreNotifications" class="empty-notifications p-3 text-center">
                  <AppIcon name="bi-inbox" class="bi bi-inbox text-muted d-block mb-2" style="font-size: 24px;" />
                  <p class="text-muted mb-0">{{ $t('no_notifications') }}</p>
                </div>
              </div>
              
              <!-- Pagination footer -->
              <div class="notification-footer" v-if="hasMorePages">
                <div class="d-flex justify-content-center w-100">
                  <button 
                    v-if="hasMorePages" 
                    @click.stop.prevent="loadMoreNotifications" 
                    class="btn-load-more"
                    :disabled="isLoadingMoreNotifications">
                    <AppIcon name="bi-arrow-repeat" v-if="isLoadingMoreNotifications" class="bi bi-arrow-repeat me-1 spin" />
                    <AppIcon name="bi-chevron-down" v-else class="bi bi-chevron-down me-1" />
                    {{ $t('load_more') }}
                  </button>
                </div>
              </div>
            </div>
          </div>



          <div class="dropdown dropleft">
            <button class="btn dropdown-toggle" style="padding:8px" type="button" id="topMenuDropdown" data-bs-toggle="dropdown" aria-expanded="false">
            <span class="d-inline-block text-secondary">
              <AppIcon name="bi-gear" class="bi bi-gear" style="font-size:20px" />
            </span>

            </button>
            <ul class="settings-dropdown dropdown-menu p-2" aria-labelledby="topMenuDropdown" style="min-width:200px">
                
                  <li class="nav-item nav-pills">
                    <router-link class="nav-link text-white p-2 d-flex align-items-center" v-if="role == 'owner'" to="/users">
                      <span class="d-inline-block text-secondary me-3">
                        <AppIcon name="bi-people-fill" class="bi bi-people-fill" style="font-size:20px" />
                      </span>
                      <span class="small">{{$t('users')}}</span>
                      </router-link>
                  </li>
                  <li class="nav-item nav-pills">
                    <router-link class="nav-link text-white p-2 d-flex align-items-center" v-if="role == 'owner'" to="/logs">
                      <span class="d-inline-block text-secondary me-3">
                        <AppIcon name="bi-file-earmark-text" class="bi bi-file-earmark-text" style="font-size:20px" />
                      </span>
                      <span class="small">{{$t('logs')}}</span>
                      </router-link>
                  </li>
                  <li class="nav-item nav-pills">
                    <a class="nav-link text-white p-2 d-flex align-items-center" href="/login">
                      <span class="d-inline-block text-secondary me-3">
                        <AppIcon name="bi-box-arrow-left" class="bi bi-box-arrow-left" style="font-size:20px" />
                      </span>
                      <span class="small">{{$t('logout')}}</span>
                    </a>
                  </li>
            </ul>
          </div>

          <!-- AI Chat Component -->
          <AIChat v-if="showAIChat" @close="toggleAIChat" />
        </div>
      </div>

    </div>
      <div  class="modal fade" id="show-inventories" tabindex="-1" aria-labelledby="show-inventories" aria-hidden="true">
            <div class="modal-dialog modal-xl">
                <div class="modal-content">

                    <div class="modal-header">
                        <h5 class="modal-title">{{$t('inventories_history')}}</h5>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="close"></button>
                    </div>

                    <inventory-history v-if="inventoryLogModalisOpen === true" ></inventory-history>
                    
                    <div class="modal-footer">
                        <button type="button" class="btn btn-secondary" data-bs-dismiss="modal"><AppIcon name="bi-x-lg" class="bi bi-x-lg me-1" />{{$t('close')}}</button>
                    </div>

                </div>
            </div>
        </div>
  </nav>
</template>


<script>
import helper from '../../helper'
import moment from 'moment'
import InventoryHistory from '../InventoryHistoryComponent.vue'
import AIChat from '../AIChat.vue'
import UniversalSearch from '../UniversalSearch.vue'
// import VideoCard from '../../components/VideoCard.vue';
export default {
  name: 'CustomNav',
  components : {
    InventoryHistory,
    AIChat,
    UniversalSearch,
    // VideoCard
  },
  data(){
    return {
        warning : [],
        inventoryLogModal : null,
        inventoryLogModalisOpen : false,
        logs : [],
        start : null,
        end : null,
        loading :false,
        per_page : 10,
        page : 1,
        count : 0,
        pagination : [],
        showSubscriptionModalisOpen : false,
        showSubscriptionModal : null,
        alertCount : 0,
        currentPage : 1,
        isLoadingNotifications : false,
        hasMorePages : false,
        isLoadingMoreNotifications : false,
        dueInvoices: [],
        dueInvoiceCount: 0,
        expiredInventories: [],
        expiredCount: 0,
        reminders: [],
        reminderCount: 0,
        reminderPoller: null,
        showAIChat: false
    }
  },
  computed : {
      role(){ return this.$store.getters.ROLE },
      business(){ return this.$store.getters.BUSINESS},
      permissions(){ return this.$store.getters.PERMISSIONS },
      businessStatus(){ return this.$store.getters.BUSINESS.status },
      user(){ return this.$store.getters.USER},
      helper(){ return helper },
      freeTrials(){ return moment(this.$store.getters.BUSINESS.trial_end_at).diff(moment(), "days" )},
      sidebarExpanded(){ return this.$store.getters.NAV_FULL_SIZE === true },
      shouldShowUniversalSearch() {
        const currentRoute = this.$route.path;
        // Hide only on /pos route (POS selling interface)
        return currentRoute !== '/pos';
      },
      totalNotifications() {
        return (this.alertCount || 0) + (this.dueInvoiceCount || 0) + (this.expiredCount || 0) + (this.reminderCount || 0);
      },
      selectedSubscriptionPlanKey() {
        return this.business?.metadata?.plan_key || this.business?.metadata?.pricing_plan || this.business?.plan_id || null;
      },
      selectedSubscriptionBillingCycle() {
        return this.business?.metadata?.billing_cycle || null;
      },
      subscribeRoute() {
        if (!this.selectedSubscriptionPlanKey) return { path: '/subscribe' };
        return {
          path: '/subscribe',
          query: {
            plan: this.selectedSubscriptionPlanKey,
            ...(this.selectedSubscriptionBillingCycle ? { billing: this.selectedSubscriptionBillingCycle } : {}),
            payment: 'true'
          }
        };
      }
  },
  created(){
    this.getUpComingOutOfStock()
    this.getDueInvoices()
    this.getExpiredInventories()
    this.getDueReminders()
  },
  mounted(){
    // eslint-disable-next-line no-undef
    this.inventoryLogModal = new bootstrap.Modal(document.getElementById('show-inventories'))
    window.addEventListener('reminders:changed', this.getDueReminders)
    this.reminderPoller = window.setInterval(this.getDueReminders, 60000)
  },
  beforeDestroy() {
    window.removeEventListener('reminders:changed', this.getDueReminders)
    if (this.reminderPoller) window.clearInterval(this.reminderPoller)
  },
  methods : {
    formatHumanDate(number) { return helper.formatDate(number,true) },
    async getUpComingOutOfStock(){
      try{
        const {
          data
        } = await helper.customGet(`${process.env.VUE_APP_API}/inventories-upcoming-os`)
        this.alertCount = data.count
        this.isLoadingNotifications = true,
        this.currentPage = data.current_page; 
        this.warning = data.data
        this.hasMorePages = this.alertCount > (data.per_page * this.currentPage);
      }finally {
        this.isLoadingNotifications = false;
      }

    },
    showInventoriesModal(){
      this.inventoryLogModalisOpen = true
      this.inventoryLogModal.show()
    },
    formatTimeAgo(timestamp) {
      return moment(timestamp).fromNow();
    },
    reminderPath(reminder) {
      const routes = {
        lead: '/leads',
        invoice: '/invoices',
        quote: '/quotes',
        project: '/projects',
      }
      const base = routes[reminder.entity_type] || '/'
      return base === '/' ? base : `${base}/${reminder.entity_id}`
    },
    async getDueReminders() {
      const response = await helper.customGet(`${process.env.VUE_APP_API}/reminders/due`)
      this.reminders = response.data.data || []
      this.reminderCount = response.data.count || this.reminders.length
    },
    async completeReminder(id) {
      await helper.customPatch(`${process.env.VUE_APP_API}/reminders/${id}`, { status: 'completed' })
      this.reminders = this.reminders.filter(reminder => reminder.id !== id)
      this.reminderCount = this.reminders.length
      window.dispatchEvent(new CustomEvent('reminders:changed'))
    },
    async loadMoreNotifications() {
      if (this.isLoadingMoreNotifications || !this.hasMorePages) return;
      
      try {
        this.isLoadingMoreNotifications = true;
        this.currentPage += 1;
        
        const { data, count, per_page, current_page } = await helper.customGet(
          `${process.env.VUE_APP_API}/inventories-upcoming-os?page=${this.currentPage}&per_page=${this.perPage}`
        );
        
        // Append new items to existing array
      this.warning = [...this.warning, ...data.data]
        this.currentPage = data.current_page; 
        
        // Check if more pages exist
        this.hasMorePages = count > (per_page * current_page);
        
      } finally {
        this.isLoadingMoreNotifications = false;
      }
    },
    async getDueInvoices(){
      try {
        const pendingReq = helper.customGet(`${process.env.VUE_APP_API}/invoices?per_page=100&page=1&status=pending`);
        const partialReq = helper.customGet(`${process.env.VUE_APP_API}/invoices?per_page=100&page=1&status=partially_paid`);

        const [pendingRes, partialRes] = await Promise.allSettled([pendingReq, partialReq]);
        const list = [];
        if (pendingRes.status === 'fulfilled' && pendingRes.value?.data?.data) list.push(...pendingRes.value.data.data);
        if (partialRes.status === 'fulfilled' && partialRes.value?.data?.data) list.push(...partialRes.value.data.data);

        // Filter for overdue or due soon (next 7 days)
        const todayStart = moment().startOf('day');
        const in7 = moment().add(7, 'days').endOf('day');
        
        this.dueInvoices = list.filter(inv => {
            if (!inv.due_date) return false;
            const due = moment(inv.due_date);
            // Overdue OR due in next 7 days
            return due.isBefore(todayStart) || due.isBetween(todayStart, in7, null, '[]');
        }).sort((a,b) => moment(a.due_date).valueOf() - moment(b.due_date).valueOf());
        
        this.dueInvoiceCount = this.dueInvoices.length;

      } catch (e) {
        alert(e)
      }
    },
    async getExpiredInventories(){
      try{
        const {
          data
        } = await helper.customGet(`${process.env.VUE_APP_API}/inventories-expired`)
        this.expiredCount = data.count
        this.expiredInventories = data.data
      } catch(e){
        alert(e)
      }
    },
    toggleAIChat() {
      this.showAIChat = !this.showAIChat;
    },
  }
}
</script>

<style scoped>  

  .settings-dropdown {
    right: 0 !important; 
    left: inherit !important;
  }

.bg-soft-primary {
  background-color: rgba(13, 110, 253, 0.1);
}

.btn-subscribe-nav {
  background: linear-gradient(135deg, #4A4A4A 0%, #004aad 100%);
  color: white;
  border: none;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.btn-subscribe-nav:hover {
  background: linear-gradient(135deg, #004aad 0%, #1a1a1a 100%);
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(48, 48, 48, 0.3);
}

.notification-dropdown {
  position: relative;
}

.notification-btn {
  position: relative;
  padding: 8px 10px;
  background: transparent;
  border: none;
  color: #495057;
  transition: all 0.2s ease;
}

.notification-btn:hover, .notification-btn:focus {
  background: rgba(0, 0, 0, 0.05);
  color: #212529;
  box-shadow: none;
}

.notification-btn::after {
  display: none;
}

.notification-btn .bi-bell-fill {
  font-size: 22px;
}

.notification-badge {
  position: absolute;
  top: 0px;
  right: 0px;
  font-size: 10px;
  font-weight: bold;
  padding: .2rem .2rem;
  border-radius: 50%;
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

.notification-menu {
  right: 0 !important;
  left: auto !important;
  width: 320px;
  padding: 0;
  border-radius: 8px;
  box-shadow: 0 5px 25px rgba(0, 0, 0, 0.15);
  border: 1px solid rgba(0, 0, 0, 0.08);
  max-height: 500px;
  overflow: hidden;
  margin-top: 12px;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #eee;
}

.notification-count {
  font-size: 12px;
  color: #6c757d;
  font-weight: 500;
}

.notification-body {
  max-height: 350px;
  overflow-y: auto;
}

.notification-item {
  padding: 12px 15px;
  display: flex;
  gap: 12px;
  border-bottom: 1px solid #eee;
  transition: background-color 0.2s ease;
  cursor: pointer;
}

.notification-item:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.notification-item.unread {
  background-color: rgba(13, 110, 253, 0.05);
}

.notification-icon {
  display: flex;
  align-items: flex-start;
  padding-top: 2px;
}

.notification-icon i {
  font-size: 16px;
}

.notification-content {
  flex: 1;
}

.notification-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
}

.product-name {
  font-weight: 600;
  font-size: 14px;
}

.notification-time {
  font-size: 11px;
  color: #6c757d;
}

.notification-text {
  font-size: 13px;
  color: #495057;
  margin-bottom: 5px;
}

.inventory-ref {
  font-weight: 500;
}

.notification-status {
  margin-top: 5px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 500;
  padding: 4px 8px;
  border-radius: 4px;
}

.out-of-stock {
  background-color: rgba(220, 53, 69, 0.1);
  color: #dc3545;
}

.low-stock {
  background-color: rgba(255, 193, 7, 0.1);
  color: #fd7e14;
}

.reminder-note {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.reminder-complete {
  border: 0;
  background-color: rgba(25, 135, 84, 0.1);
  color: #198754;
  cursor: pointer;
}

.reminder-complete:hover {
  background-color: rgba(25, 135, 84, 0.18);
}

.notification-footer {
  display: flex;
  justify-content: space-between;
  padding: 10px 15px;
  background-color: #f8f9fa;
  border-top: 1px solid #eee;
}

.notification-footer a {
  font-size: 12px;
  text-decoration: none;
  color: #0d6efd;
}

/* Add these styles to your existing styles */
.notification-body {
  max-height: 350px;
  overflow-y: auto;
  min-height: 100px; /* Ensures there's space even when empty */
}

.btn-load-more {
  background: transparent;
  border: none;
  color: #0d6efd;
  font-size: 13px;
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 4px;
}

.btn-load-more:hover {
  background-color: rgba(13, 110, 253, 0.1);
}

.btn-load-more:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.spin {
  animation: spinner 1s linear infinite;
}

@keyframes spinner {
  to {
    transform: rotate(360deg);
  }
}

.empty-notifications {
  color: #6c757d;
}

.notification-footer {
  display: flex;
  padding: 10px 15px;
  background-color: #f8f9fa;
  border-top: 1px solid #eee;
}

/* Universal Search Positioning */
.custom-nav {
  position: relative;
  top: 0;
  right: 0;
  z-index: 140;
  width: 100%;
  transition: all 0.3s ease;
}

.universal-search-container {
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  transition: all 0.3s ease;
  z-index: 145;
}

/* When sidebar is expanded (14.5rem = 232px) */
.nav-sidebar-expanded .universal-search-container {
  left: 15rem; /* 14.5rem sidebar + 0.5rem spacing */
}

/* When sidebar is collapsed (3rem = 48px) */
.nav-sidebar-collapsed .universal-search-container {
  left: 3.5rem; /* 3rem sidebar + 0.5rem spacing */
}

/* RTL Support */
[dir="rtl"] .custom-nav {
  right: auto;
  left: 0;
}

[dir="rtl"] .universal-search-container {
  left: auto;
  right: 0;
}

[dir="rtl"] .nav-sidebar-expanded .universal-search-container {
  right: 15rem; /* Next to expanded sidebar (on the right side in RTL) */
}

[dir="rtl"] .nav-sidebar-collapsed .universal-search-container {
  right: 3.5rem; /* Next to collapsed sidebar (on the right side in RTL) */
}

/* Mobile responsiveness - Keep fixed on mobile */
@media (max-width: 991.98px) {
  .custom-nav {
    position: fixed;
  }
  
  .universal-search-container {
    left: 1rem !important;
    right: auto !important;
  }
  
  [dir="rtl"] .universal-search-container {
    right: 1rem !important;
    left: auto !important;
  }
}

.tutorials-nav-btn {
  position: relative;
  padding: 8px 10px;
  background: transparent;
  border: none;
  color: #495057;
  transition: all 0.2s ease;
}

.tutorials-nav-btn:hover,
.tutorials-nav-btn:focus {
  background: rgba(0, 0, 0, 0.05);
  color: #212529;
  box-shadow: none;
}
</style>
